/**
 * Hover, press and text colours for a filled control (primary or danger
 * button, checked toggle) derived from one theme colour, plus the accent as
 * readable link text (#1127).
 *
 * Themes only define the fill (e.g. `accent_color`). The text on it must stay
 * readable for any fill a theme author picks — white on a yellow accent is
 * not. So the foreground is not a fixed white but one of the theme's own two
 * poles (its text and background colour), whichever contrasts better with
 * the fill in its resting and pressed state. A pole that misses WCAG AA
 * (4.5:1) — a brand red against a cream text colour lands at 4.4 — is first
 * pushed a few steps further away from the fill; the better result wins.
 */
import { contrastRatio, hexToRgb, relativeLuminance, rgbToHex, shiftHex } from '@/lib/color'

export interface FillTokens {
  hover: string
  active: string
  foreground: string
  /** Toggle thumb on the fill: the theme's lighter pole where it reaches 3:1, else `foreground`. */
  knob: string
}

/** Channel shift for hover; it moves the fill away from the text colour. */
const HOVER_SHIFT = 20
/** Channel shift for press; pressed is always a little darker. */
const PRESS_SHIFT = -14
/** WCAG AA for normal-size text. */
const AA = 4.5
/** WCAG minimum for non-text UI parts such as a toggle thumb. */
const UI_CONTRAST = 3
/** Step and limit for pushing a pole that misses AA away from the fill. */
const PUSH_STEP = 8
const PUSH_MAX_STEPS = 6

function worstContrast(text: string, fills: readonly string[]): number | null {
  let worst = Infinity
  for (const fill of fills) {
    const ratio = contrastRatio(text, fill)
    if (ratio === null) return null
    worst = Math.min(worst, ratio)
  }
  return worst
}

/** A pole, pushed away from the fill's luminance until it reaches AA (or the step limit). */
function pushToAA(pole: string, fills: readonly string[], fillLum: number) {
  const poleLum = relativeLuminance(pole)
  let contrast = worstContrast(pole, fills)
  if (poleLum === null || contrast === null) return null
  const direction = poleLum > fillLum ? 1 : -1
  let color = pole
  for (let step = 0; step < PUSH_MAX_STEPS && contrast < AA; step++) {
    color = shiftHex(color, direction * PUSH_STEP)
    contrast = worstContrast(color, fills) ?? contrast
  }
  return { color, contrast }
}

/**
 * @param fill   the theme colour of the fill, 6-digit hex
 * @param poles  candidate text colours from the same theme (text and background colour)
 * @returns null when `fill` or every pole is not a 6-digit hex colour
 */
export function deriveFillTokens(fill: string, poles: readonly string[]): FillTokens | null {
  const fillLum = relativeLuminance(fill)
  if (fillLum === null) return null

  const active = shiftHex(fill, PRESS_SHIFT)
  const fills = [fill, active]
  let foreground: string | null = null
  let best = 0
  for (const pole of poles) {
    const candidate = pushToAA(pole, fills, fillLum)
    if (candidate && candidate.contrast > best) {
      best = candidate.contrast
      foreground = candidate.color
    }
  }
  if (foreground === null) return null

  const direction = (relativeLuminance(foreground) ?? 0) > fillLum ? 1 : -1
  // Hover moves the fill away from the text, so it never loses contrast.
  const hover = shiftHex(fill, direction > 0 ? -HOVER_SHIFT : HOVER_SHIFT)

  // A light thumb is what a switch usually looks like; keep it wherever it shows.
  const lighterPole = [...poles]
    .filter((pole) => relativeLuminance(pole) !== null)
    .sort((a, b) => (relativeLuminance(b) ?? 0) - (relativeLuminance(a) ?? 0))[0]
  const knob = lighterPole && (contrastRatio(lighterPole, fill) ?? 0) >= UI_CONTRAST
    ? lighterPole
    : foreground
  return { hover, active, foreground, knob }
}

/** Step and limit for pulling an accent toward the text colour. */
const MIX_STEP = 0.08
const MIX_MAX = 0.64

function mixHex(a: string, b: string, weightOfB: number): string {
  const ca = hexToRgb(a)
  const cb = hexToRgb(b)
  const mix = (x: number, y: number) => x + (y - x) * weightOfB
  return rgbToHex({ r: mix(ca.r, cb.r), g: mix(ca.g, cb.g), b: mix(ca.b, cb.b) }).toLowerCase()
}

/**
 * The accent as text (links) on the theme's background: the accent itself
 * when it reaches AA there, otherwise pulled toward the theme's text colour
 * just far enough — a yellow accent becomes a dark gold on white.
 *
 * @returns null when any colour is not a 6-digit hex colour
 */
export function readableAccentText(accent: string, background: string, text: string): string | null {
  if (contrastRatio(accent, background) === null || contrastRatio(text, background) === null) return null
  let weight = 0
  let candidate = accent
  while ((contrastRatio(candidate, background) ?? 0) < AA && weight < MIX_MAX) {
    weight = Math.min(MIX_MAX, weight + MIX_STEP)
    candidate = mixHex(accent, text, weight)
  }
  return candidate
}
